import * as React from 'react';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';
import FormErrorMessage from '../../../components/ErrorMessage';
import DateTimeInput from '../../../components/DateTimeInput';

// TYPES
import { closeModalType } from '../../../components/GlobalModal';
import { IncidentFormTypes } from '../IncidentSubmit';

interface AddMedicationModalTypes {
	closeModal?: closeModalType;
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
}

const AddMedicationModal: React.FC<AddMedicationModalTypes> = ({
	closeModal,
	incidentFormData,
	setIncidentFormData
}) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const initialValues = {
		name: '',
		dosage: '',
		dateAndTime: '',
		personAdministered: '',
		role: ''
	};

	const validationSchema = Yup.object({
		name: Yup.string().required('Required'),
		dosage: Yup.string().required('Required'),
		dateAndTime: Yup.string().required('Required'),
		personAdministered: Yup.string().required('Required'),
		role: Yup.string().required('Required')
	});

	const submitHandler = (values: any) => {
		const formData = {
			name: values.name as string,
			dosage: values.dosage as string,
			dateAndTime: values.dateAndTime as string,
			personAdministered: values.personAdministered as string,
			role: values.role as string
		};

		const currentMedication = incidentFormData.medication || [];

		const newMedication = [...currentMedication, formData];

		setIncidentFormData((prevState) => ({
			...prevState,

			medication: newMedication
		}));

		if (closeModal) {
			closeModal();
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={submitHandler}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form mtop-0">
						<>
							{showError ? (
								<FormErrorMessage message={formErrorMessage} />
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<div className="row">
													<div className="form-group col-md-6">
														<label htmlFor="name" className="label">
															Medication administered
														</label>

														<div className="form-field mbtom-0">
															<Field name="name" id="name" type="text" className="form-input" />

															<ErrorMessage name="name" component="div" className="error-message" />
														</div>
													</div>

													<div className="form-group col-md-6">
														<label htmlFor="dosage" className="label">
															How much was the dosage?
														</label>

														<div className="form-field mbtom-0">
															<Field name="dosage" id="dosage" type="text" className="form-input" />

															<ErrorMessage name="dosage" component="div" className="error-message" />
														</div>
													</div>
												</div>

												<div className="form-group">
													<label className="label">Date and time medication was administered</label>

													<DateTimeInput
														dateTimeString={formik.values.dateAndTime}
														onChange={(val: string) => formik.setFieldValue('dateAndTime', val)}
														excludeFuture
													/>
												</div>

												<div className="form-group">
													<label htmlFor="personAdministered" className="label">
														Person administering medication
													</label>

													<div className="form-field">
														<Field
															name="personAdministered"
															id="personAdministered"
															type="text"
															className="form-input"
														/>

														<ErrorMessage name="personAdministered" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="role" className="label">
														Role
													</label>

													<div className="form-field">
														<Field
															name="role"
															id="role"
															type="text"
															className="form-input"
															placeholder={'E.g. paramedic, nurse, general practitioner...'}
														/>

														<ErrorMessage name="role" component="div" className="error-message" />
													</div>
												</div>

												<Button className="btn-primary full-width" type="submit">
													Add to the list
												</Button>
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</>
					</Form>
				);
			}}
		</Formik>
	);
};

export default AddMedicationModal;
